
import React, { useState } from 'react';
import { Shield, Lock, CreditCard, Settings, LogOut, Pencil } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';

const ProfileSection = () => {
  const { user, signOut } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isSigningOut, setIsSigningOut] = useState(false);
  
  const handleSignOut = async () => {
    try {
      setIsSigningOut(true);
      await signOut();
      navigate('/signin');
    } catch (error) {
      console.error('Error signing out:', error);
      toast({
        title: 'Error',
        description: 'Failed to sign out. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSigningOut(false);
    }
  };
  
  // Get user initials for avatar fallback
  const getUserInitials = () => {
    if (!user?.email) return 'U';
    return user.email.substring(0, 2).toUpperCase();
  };
  
  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col items-center space-y-4 relative">
        <Avatar className="w-24 h-24 border-4 border-white shadow-lg">
          <AvatarImage src={user?.avatar_url} />
          <AvatarFallback className="text-2xl bg-gradient-to-tr from-blue-500 to-blue-400 text-white">
            {getUserInitials()}
          </AvatarFallback>
        </Avatar>
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900">
            {user?.username || user?.email || 'Guest User'}
          </h2>
          <p className="text-gray-500">{user?.email || 'Not signed in'}</p>
        </div>
        <Button 
          variant="outline" 
          size="sm" 
          className="absolute top-0 right-0 rounded-full gap-1"
          onClick={() => navigate('/profile/edit')}
        >
          <Pencil size={14} />
          Edit
        </Button>
      </div>
      
      <div className="bg-white rounded-2xl shadow-sm border border-gray-50 overflow-hidden">
        <div className="divide-y divide-gray-100">
          <ProfileItem 
            icon={<Shield size={20} className="text-blue-500" />}
            title="Security & Privacy"
            description="End-to-end encrypted payments"
          />
          <ProfileItem 
            icon={<Lock size={20} className="text-blue-500" />}
            title="Bluetooth Security"
            description="Secure connection preferences"
          />
          <ProfileItem 
            icon={<CreditCard size={20} className="text-blue-500" />}
            title="Payment Methods"
            description="Connected accounts and cards"
          />
          <ProfileItem 
            icon={<Settings size={20} className="text-blue-500" />}
            title="App Settings"
            description="Notifications, theme, and more"
          />
        </div>
      </div>
      
      <div className="p-4 rounded-xl bg-blue-50 flex">
        <div className="w-10 h-10 rounded-full bg-blue-100 flex-shrink-0 flex items-center justify-center mr-4">
          <Lock size={18} className="text-blue-600" />
        </div>
        <div>
          <h3 className="font-medium text-gray-900">Your data is secure</h3>
          <p className="text-sm text-gray-600 mt-1">All transactions are encrypted and your payment information is never stored on our servers.</p>
        </div>
      </div>
      
      {user ? (
        <button 
          className="w-full py-3 text-center text-red-600 font-medium flex items-center justify-center gap-2"
          onClick={handleSignOut}
          disabled={isSigningOut}
        >
          <LogOut size={18} />
          {isSigningOut ? 'Signing Out...' : 'Sign Out'}
        </button>
      ) : (
        <button 
          className="w-full py-3 text-center text-blue-600 font-medium"
          onClick={() => navigate('/signin')}
        >
          Sign In
        </button>
      )}
    </div>
  );
};

interface ProfileItemProps {
  icon: React.ReactNode;
  title: string;
  description: string;
}

const ProfileItem = ({ icon, title, description }: ProfileItemProps) => (
  <div className="flex items-center p-4 cursor-pointer hover:bg-gray-50 transition-colors">
    <div className="w-10 h-10 rounded-full bg-blue-50 flex-shrink-0 flex items-center justify-center mr-4">
      {icon}
    </div>
    <div className="flex-grow">
      <h3 className="font-medium text-gray-900">{title}</h3>
      <p className="text-sm text-gray-500">{description}</p>
    </div>
    <div className="text-gray-400">
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
      </svg>
    </div>
  </div>
);

export default ProfileSection;
